import React, { useEffect, useState, useRef } from "react";
import {
  Diamond,
  Network,
  Shield,
  Globe,
  Layers,
  CheckCircle,
  ArrowRight,
  TrendingDown,
  AlertTriangle,
  Landmark,
  Scale,
  Zap,
  Code,
  Coins,
  KeyRound,
  TrendingUp,
  Clock,
  Users,
  Wifi,
  CircleDot,
} from "lucide-react";

const iconMap = {
  diamond: Diamond,
  network: Network,
  shield: Shield,
  globe: Globe,
  layers: Layers,
  check: CheckCircle,
  code: Code,
  landmark: Landmark,
  coins: Coins,
  key: KeyRound,
};

const AnimatedCounter = ({ value, duration = 2000 }) => {
  const [display, setDisplay] = useState("0");
  const ref = useRef(null);

  useEffect(() => {
    const numericMatch = value.match(/[\d,.]+/);
    if (!numericMatch) {
      setDisplay(value);
      return;
    }

    const prefix = value.slice(0, value.indexOf(numericMatch[0]));
    const suffix = value.slice(
      value.indexOf(numericMatch[0]) + numericMatch[0].length
    );
    const target = parseFloat(numericMatch[0].replace(/,/g, ""));
    const hasComma = numericMatch[0].includes(",");
    let start = 0;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(start + (target - start) * eased);

      let formatted = hasComma ? current.toLocaleString() : String(current);
      setDisplay(`${prefix}${formatted}${suffix}`);

      if (progress < 1) {
        ref.current = requestAnimationFrame(animate);
      }
    };

    ref.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(ref.current);
  }, [value, duration]);

  return <span>{display}</span>;
};

const StaggeredItem = ({ children, index }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 150 + index * 120);
    return () => clearTimeout(timer);
  }, [index]);

  return (
    <div className={`stagger-item ${visible ? "stagger-visible" : ""}`}>
      {children}
    </div>
  );
};

/* ========== HERO SLIDE ========== */
const HeroSlide = ({ slide, onSkipTo, onNext }) => {
  const SplineComponent = React.lazy(() =>
    import("@splinetool/react-spline").then((mod) => ({
      default: mod.default,
    }))
  );

  return (
    <div className="slide-hero">
      <div className="slide-hero-content">
        <StaggeredItem index={0}>
          <span className="slide-tag">{slide.tag}</span>
        </StaggeredItem>
        <StaggeredItem index={1}>
          <h1 className="slide-hero-title">
            {slide.title.split("\n").map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                {line}
              </React.Fragment>
            ))}
          </h1>
        </StaggeredItem>
        <StaggeredItem index={2}>
          <p className="slide-hero-subtitle">{slide.subtitle}</p>
        </StaggeredItem>
        <StaggeredItem index={3}>
          <div className="slide-hero-actions">
            <button className="btn-primary-pres" onClick={onNext}>
              {slide.cta}
              <ArrowRight size={18} />
            </button>
          </div>
        </StaggeredItem>
      </div>
      <div className="slide-hero-visual">
        <React.Suspense
          fallback={
            <div className="spline-loader">
              <div className="spline-loader-ring" />
            </div>
          }
        >
          <SplineComponent scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
        </React.Suspense>
      </div>
    </div>
  );
};

/* ========== STATEMENT SLIDE ========== */
const StatementSlide = ({ slide }) => (
  <div className="slide-statement">
    <div className="slide-statement-left">
      <StaggeredItem index={0}>
        <span className="slide-tag">{slide.tag}</span>
      </StaggeredItem>
      <StaggeredItem index={1}>
        <h2 className="slide-title">
          {slide.title.split("\n").map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </h2>
      </StaggeredItem>
      <StaggeredItem index={2}>
        <div className="slide-fact-card">
          <span className="slide-fact-label">{slide.factLabel}</span>
          <span className="slide-fact-value">
            <AnimatedCounter value={slide.factValue} />
          </span>
          <span className="slide-fact-note">{slide.factNote}</span>
        </div>
      </StaggeredItem>
    </div>
    <div className="slide-statement-right">
      <StaggeredItem index={1}>
        <p className="slide-body">{slide.body}</p>
      </StaggeredItem>
      <StaggeredItem index={2}>
        <div className="slide-highlight">
          <Landmark size={24} className="slide-highlight-icon" />
          <p>{slide.highlight}</p>
        </div>
      </StaggeredItem>
    </div>
  </div>
);

/* ========== EVENT SLIDE ========== */
const EventSlide = ({ slide }) => (
  <div className="slide-event">
    <div className="slide-event-top">
      <StaggeredItem index={0}>
        <span className="slide-tag">{slide.tag}</span>
      </StaggeredItem>
      <StaggeredItem index={1}>
        <h2 className="slide-event-date">{slide.title}</h2>
      </StaggeredItem>
      <StaggeredItem index={2}>
        <p className="slide-event-subtitle">{slide.subtitle}</p>
      </StaggeredItem>
      <StaggeredItem index={3}>
        <p className="slide-body slide-event-body">{slide.body}</p>
      </StaggeredItem>
    </div>
    <div className="slide-stats-row">
      {slide.stats.map((stat, i) => (
        <StaggeredItem key={i} index={i + 3}>
          <div className="slide-stat-card">
            <span className="slide-stat-value">
              <AnimatedCounter value={stat.value} />
            </span>
            <span className="slide-stat-label">{stat.label}</span>
          </div>
        </StaggeredItem>
      ))}
    </div>
  </div>
);

/* ========== BREAKDOWN SLIDE ========== */
const BreakdownSlide = ({ slide }) => (
  <div className="slide-breakdown">
    <div className="slide-breakdown-top">
      <StaggeredItem index={0}>
        <span className="slide-tag">{slide.tag}</span>
      </StaggeredItem>
      <StaggeredItem index={1}>
        <h2 className="slide-title">
          {slide.title.split("\n").map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </h2>
      </StaggeredItem>
      <StaggeredItem index={2}>
        <p className="slide-body">{slide.body}</p>
      </StaggeredItem>
    </div>
    <div className="slide-comparisons-grid">
      {slide.comparisons.map((comp, i) => (
        <StaggeredItem key={i} index={i + 2}>
          <div
            className={`slide-comparison-card ${comp.year === "2024" ? "comparison-highlight" : ""}`}
          >
            <span className="comparison-year">{comp.year}</span>
            <span className="comparison-item">{comp.item}</span>
            <span className="comparison-price">{comp.price}</span>
          </div>
        </StaggeredItem>
      ))}
    </div>
    <StaggeredItem index={8}>
      <blockquote className="slide-quote">
        <TrendingDown size={20} className="quote-icon" />
        {slide.quote}
      </blockquote>
    </StaggeredItem>
  </div>
);

/* ========== MECHANISM SLIDE ========== */
const MechanismSlide = ({ slide }) => (
  <div className="slide-mechanism">
    <div className="slide-mechanism-header">
      <StaggeredItem index={0}>
        <span className="slide-tag">{slide.tag}</span>
      </StaggeredItem>
      <StaggeredItem index={1}>
        <h2 className="slide-title">
          {slide.title.split("\n").map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </h2>
      </StaggeredItem>
      <StaggeredItem index={2}>
        <p className="slide-body slide-mechanism-body">{slide.body}</p>
      </StaggeredItem>
    </div>
    <div className="slide-steps">
      {slide.steps.map((step, i) => (
        <StaggeredItem key={i} index={i + 2}>
          <div className="slide-step">
            <span className="step-number">{step.number}</span>
            <div className="step-content">
              <h4 className="step-title">{step.title}</h4>
              <p className="step-desc">{step.description}</p>
            </div>
            {i < slide.steps.length - 1 && (
              <div className="step-connector" />
            )}
          </div>
        </StaggeredItem>
      ))}
    </div>
    <StaggeredItem index={7}>
      <div className="slide-bottom-fact">
        <AlertTriangle size={20} />
        <span>{slide.bottomFact}</span>
      </div>
    </StaggeredItem>
  </div>
);

/* ========== POWER SLIDE ========== */
const PowerSlide = ({ slide }) => (
  <div className="slide-power">
    <div className="slide-power-left">
      <StaggeredItem index={0}>
        <span className="slide-tag">{slide.tag}</span>
      </StaggeredItem>
      <StaggeredItem index={1}>
        <h2 className="slide-title">
          {slide.title.split("\n").map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </h2>
      </StaggeredItem>
      <StaggeredItem index={2}>
        <p className="slide-body">{slide.body}</p>
      </StaggeredItem>
    </div>
    <div className="slide-power-right">
      {slide.abuses.map((abuse, i) => (
        <StaggeredItem key={i} index={i + 2}>
          <div className="slide-abuse-card">
            <div className="abuse-header">
              <span className="abuse-event">{abuse.event}</span>
              <span className="abuse-amount">{abuse.amount}</span>
            </div>
            <p className="abuse-detail">{abuse.detail}</p>
          </div>
        </StaggeredItem>
      ))}
    </div>
  </div>
);

/* ========== SOLUTION SLIDE ========== */
const SolutionSlide = ({ slide }) => (
  <div className="slide-solution">
    <div className="slide-solution-content">
      <StaggeredItem index={0}>
        <span className="slide-tag">{slide.tag}</span>
      </StaggeredItem>
      <StaggeredItem index={1}>
        <h2 className="slide-solution-title">{slide.title}</h2>
      </StaggeredItem>
      <StaggeredItem index={2}>
        <p className="slide-solution-subtitle">{slide.subtitle}</p>
      </StaggeredItem>
      <StaggeredItem index={3}>
        <p className="slide-body">{slide.body}</p>
      </StaggeredItem>
      <div className="slide-principles">
        {slide.principles.map((p, i) => (
          <StaggeredItem key={i} index={i + 4}>
            <div className="slide-principle">
              <Zap size={16} className="principle-icon" />
              <span>{p}</span>
            </div>
          </StaggeredItem>
        ))}
      </div>
    </div>
    <div className="slide-solution-visual">
      <div className="bitcoin-symbol-large">
        <span>&#x20BF;</span>
      </div>
    </div>
  </div>
);

/* ========== PROPERTIES SLIDE ========== */
const PropertiesSlide = ({ slide }) => (
  <div className="slide-properties">
    <div className="slide-properties-header">
      <StaggeredItem index={0}>
        <span className="slide-tag">{slide.tag}</span>
      </StaggeredItem>
      <StaggeredItem index={1}>
        <h2 className="slide-title">
          {slide.title.split("\n").map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </h2>
      </StaggeredItem>
    </div>
    <div className="slide-properties-grid">
      {slide.properties.map((prop, i) => {
        const IconComp = iconMap[prop.icon] || Diamond;
        return (
          <StaggeredItem key={i} index={i + 1}>
            <div className="slide-property-card">
              <div className="property-icon-wrap">
                <IconComp size={22} />
              </div>
              <div className="property-info">
                <div className="property-top">
                  <h4 className="property-name">{prop.name}</h4>
                  <span className="property-value">{prop.value}</span>
                </div>
                <p className="property-desc">{prop.description}</p>
              </div>
            </div>
          </StaggeredItem>
        );
      })}
    </div>
  </div>
);

/* ========== COMPARISON SLIDE ========== */
const ComparisonSlide = ({ slide }) => {
  const fields = ["supply", "censorship", "seizure", "portability", "verification"];
  const fieldLabels = {
    supply: "Supply",
    censorship: "Censorship",
    seizure: "Seizure",
    portability: "Portability",
    verification: "Verification",
  };

  return (
    <div className="slide-comparison">
      <StaggeredItem index={0}>
        <span className="slide-tag">{slide.tag}</span>
      </StaggeredItem>
      <StaggeredItem index={1}>
        <h2 className="slide-title">
          {slide.title.split("\n").map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </h2>
      </StaggeredItem>
      <StaggeredItem index={2}>
        <div className="slide-comparison-table">
          <div className="comp-table-header">
            <div className="comp-cell comp-label-cell" />
            {slide.assets.map((asset, i) => (
              <div
                key={i}
                className={`comp-cell comp-header-cell ${asset.highlight ? "comp-highlight" : ""}`}
              >
                {asset.name}
              </div>
            ))}
          </div>
          {fields.map((field, fi) => (
            <StaggeredItem key={fi} index={fi + 3}>
              <div className="comp-table-row">
                <div className="comp-cell comp-label-cell">
                  {fieldLabels[field]}
                </div>
                {slide.assets.map((asset, ai) => (
                  <div
                    key={ai}
                    className={`comp-cell ${asset.highlight ? "comp-highlight-cell" : ""}`}
                  >
                    {asset[field]}
                  </div>
                ))}
              </div>
            </StaggeredItem>
          ))}
        </div>
      </StaggeredItem>
      <StaggeredItem index={9}>
        <div className="slide-bottom-line">
          <Scale size={20} />
          <span>{slide.bottomLine}</span>
        </div>
      </StaggeredItem>
    </div>
  );
};

/* ========== CLOSING SLIDE ========== */
const ClosingSlide = ({ slide }) => (
  <div className="slide-closing">
    <div className="slide-closing-content">
      <StaggeredItem index={0}>
        <span className="slide-tag">{slide.tag}</span>
      </StaggeredItem>
      <StaggeredItem index={1}>
        <h2 className="slide-closing-title">
          {slide.title.split("\n").map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </h2>
      </StaggeredItem>
      <StaggeredItem index={2}>
        <p className="slide-body slide-closing-body">{slide.body}</p>
      </StaggeredItem>
      <StaggeredItem index={3}>
        <blockquote className="slide-final-quote">{slide.finalQuote}</blockquote>
      </StaggeredItem>
      <StaggeredItem index={4}>
        <a
          href={slide.ctaLink}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary-pres btn-closing"
        >
          {slide.cta}
          <ArrowRight size={18} />
        </a>
      </StaggeredItem>
    </div>
  </div>
);

/* ========== WEALTH GAP SLIDE ========== */
const WealthGapSlide = ({ slide }) => (
  <div className="slide-wealthgap">
    <div className="slide-wealthgap-left">
      <StaggeredItem index={0}>
        <span className="slide-tag">{slide.tag}</span>
      </StaggeredItem>
      <StaggeredItem index={1}>
        <h2 className="slide-title">
          {slide.title.split("\n").map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </h2>
      </StaggeredItem>
      <StaggeredItem index={2}>
        <p className="slide-body">{slide.body}</p>
      </StaggeredItem>
    </div>
    <div className="slide-wealthgap-right">
      {slide.metrics.map((m, i) => (
        <StaggeredItem key={i} index={i + 2}>
          <div className="wealthgap-metric">
            <span className="wealthgap-label">{m.label}</span>
            {m.before ? (
              <div className="wealthgap-comparison">
                <div className="wealthgap-before">
                  <span className="wealthgap-val">{m.before}</span>
                  <span className="wealthgap-year">{m.beforeYear}</span>
                </div>
                <ArrowRight size={18} className="wealthgap-arrow" />
                <div className="wealthgap-after">
                  <span className="wealthgap-val wealthgap-val-accent">{m.after}</span>
                  <span className="wealthgap-year">{m.afterYear}</span>
                </div>
              </div>
            ) : (
              <span className="wealthgap-value">{m.value}</span>
            )}
            {m.detail && <p className="wealthgap-detail">{m.detail}</p>}
          </div>
        </StaggeredItem>
      ))}
    </div>
    <StaggeredItem index={6}>
      <div className="slide-bottom-fact slide-bottom-fact-wide">
        <AlertTriangle size={20} />
        <span>{slide.bottomFact}</span>
      </div>
    </StaggeredItem>
  </div>
);

/* ========== HALVING SLIDE ========== */
const HalvingSlide = ({ slide }) => (
  <div className="slide-halving">
    <div className="slide-halving-header">
      <StaggeredItem index={0}>
        <span className="slide-tag">{slide.tag}</span>
      </StaggeredItem>
      <StaggeredItem index={1}>
        <h2 className="slide-halving-title">{slide.title}</h2>
      </StaggeredItem>
      <StaggeredItem index={2}>
        <p className="slide-halving-subtitle">{slide.subtitle}</p>
      </StaggeredItem>
      <StaggeredItem index={3}>
        <p className="slide-body slide-halving-body">{slide.body}</p>
      </StaggeredItem>
    </div>
    <div className="slide-halving-timeline">
      {slide.halvings.map((h, i) => (
        <StaggeredItem key={i} index={i + 3}>
          <div className={`halving-node ${i === slide.halvings.length - 1 ? "halving-future" : ""}`}>
            <div className="halving-dot" />
            <span className="halving-year">{h.year}</span>
            <span className="halving-reward">{h.reward}</span>
            <span className="halving-event">{h.event}</span>
            <span className="halving-price">{h.priceAtTime}</span>
          </div>
        </StaggeredItem>
      ))}
    </div>
    <StaggeredItem index={10}>
      <div className="slide-bottom-line">
        <TrendingUp size={20} />
        <span>{slide.insight}</span>
      </div>
    </StaggeredItem>
  </div>
);

/* ========== TRACK RECORD SLIDE ========== */
const TrackRecordSlide = ({ slide }) => (
  <div className="slide-trackrecord">
    <div className="slide-trackrecord-top">
      <StaggeredItem index={0}>
        <span className="slide-tag">{slide.tag}</span>
      </StaggeredItem>
      <StaggeredItem index={1}>
        <h2 className="slide-title">
          {slide.title.split("\n").map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </h2>
      </StaggeredItem>
      <StaggeredItem index={2}>
        <p className="slide-trackrecord-subtitle">{slide.subtitle}</p>
      </StaggeredItem>
    </div>
    <div className="slide-trackrecord-content">
      <div className="trackrecord-milestones">
        {slide.milestones.map((m, i) => (
          <StaggeredItem key={i} index={i + 2}>
            <div className="milestone-row">
              <span className="milestone-year">{m.year}</span>
              <span className="milestone-price">{m.price}</span>
              <span className="milestone-event">{m.event}</span>
            </div>
          </StaggeredItem>
        ))}
      </div>
      <div className="trackrecord-adoption">
        {slide.adoptionStats.map((s, i) => (
          <StaggeredItem key={i} index={i + 7}>
            <div className="adoption-stat">
              <span className="adoption-value">
                <AnimatedCounter value={s.value} />
              </span>
              <span className="adoption-label">{s.label}</span>
            </div>
          </StaggeredItem>
        ))}
      </div>
    </div>
    <StaggeredItem index={12}>
      <div className="slide-bottom-line">
        <TrendingUp size={20} />
        <span>{slide.bottomLine}</span>
      </div>
    </StaggeredItem>
  </div>
);

/* ========== CRYPTO INTRO SLIDE ========== */
const CryptoIntroSlide = ({ slide }) => (
  <div className="slide-cryptointro">
    <div className="slide-cryptointro-header">
      <StaggeredItem index={0}>
        <span className="slide-tag">{slide.tag}</span>
      </StaggeredItem>
      <StaggeredItem index={1}>
        <h2 className="slide-title">
          {slide.title.split("\n").map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </h2>
      </StaggeredItem>
      <StaggeredItem index={2}>
        <p className="slide-cryptointro-subtitle">{slide.subtitle}</p>
      </StaggeredItem>
      <StaggeredItem index={3}>
        <p className="slide-body">{slide.body}</p>
      </StaggeredItem>
    </div>
    <div className="slide-pillars-grid">
      {slide.pillars.map((p, i) => {
        const IconComp = iconMap[p.icon] || CircleDot;
        return (
          <StaggeredItem key={i} index={i + 3}>
            <div className="pillar-card">
              <div className="pillar-icon-wrap">
                <IconComp size={22} />
              </div>
              <h4 className="pillar-name">{p.name}</h4>
              <p className="pillar-desc">{p.description}</p>
            </div>
          </StaggeredItem>
        );
      })}
    </div>
  </div>
);

/* ========== DEFI SLIDE ========== */
const DefiSlide = ({ slide }) => (
  <div className="slide-defi">
    <div className="slide-defi-header">
      <StaggeredItem index={0}>
        <span className="slide-tag">{slide.tag}</span>
      </StaggeredItem>
      <StaggeredItem index={1}>
        <h2 className="slide-title">
          {slide.title.split("\n").map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </h2>
      </StaggeredItem>
      <StaggeredItem index={2}>
        <p className="slide-body">{slide.body}</p>
      </StaggeredItem>
    </div>
    <div className="slide-defi-content">
      <div className="defi-stats-grid">
        {slide.defiStats.map((s, i) => (
          <StaggeredItem key={i} index={i + 2}>
            <div className="defi-stat">
              <span className="defi-stat-value">{s.value}</span>
              <span className="defi-stat-label">{s.label}</span>
            </div>
          </StaggeredItem>
        ))}
      </div>
      <div className="defi-usecases">
        {slide.useCases.map((u, i) => (
          <StaggeredItem key={i} index={i + 6}>
            <div className="defi-usecase">
              <h4 className="defi-usecase-title">{u.title}</h4>
              <p className="defi-usecase-desc">{u.description}</p>
            </div>
          </StaggeredItem>
        ))}
      </div>
    </div>
  </div>
);

/* ========== FUTURE SLIDE ========== */
const FutureSlide = ({ slide }) => (
  <div className="slide-future">
    <div className="slide-future-header">
      <StaggeredItem index={0}>
        <span className="slide-tag">{slide.tag}</span>
      </StaggeredItem>
      <StaggeredItem index={1}>
        <h2 className="slide-title">
          {slide.title.split("\n").map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </h2>
      </StaggeredItem>
      <StaggeredItem index={2}>
        <p className="slide-body">{slide.body}</p>
      </StaggeredItem>
    </div>
    <div className="slide-trends-grid">
      {slide.trends.map((t, i) => (
        <StaggeredItem key={i} index={i + 2}>
          <div className="trend-card">
            <div className="trend-header">
              <h4 className="trend-title">{t.title}</h4>
              <span className={`trend-status trend-status-${t.status.toLowerCase().replace(/\s/g, "-")}`}>
                {t.status}
              </span>
            </div>
            <p className="trend-desc">{t.description}</p>
          </div>
        </StaggeredItem>
      ))}
    </div>
  </div>
);

/* ========== MAIN ROUTER ========== */
export const SlideContent = ({ slide, onSkipTo, onNext }) => {
  const slideMap = {
    hero: HeroSlide,
    statement: StatementSlide,
    event: EventSlide,
    breakdown: BreakdownSlide,
    mechanism: MechanismSlide,
    power: PowerSlide,
    wealthgap: WealthGapSlide,
    solution: SolutionSlide,
    halving: HalvingSlide,
    properties: PropertiesSlide,
    trackrecord: TrackRecordSlide,
    comparison: ComparisonSlide,
    cryptointro: CryptoIntroSlide,
    defi: DefiSlide,
    future: FutureSlide,
    closing: ClosingSlide,
  };

  const Component = slideMap[slide.type];
  if (!Component) return null;

  return <Component slide={slide} onSkipTo={onSkipTo} onNext={onNext} />;
};
